import {Helmet} from '@dr.pogodin/react-helmet';

import {useParams} from 'react-router-dom';

import {isNil} from 'ramda';
import {isNilOrEmpty, isNotNil} from 'ramda-adjunct';

import {useGetMarketingCodeQuery, useGetWarehousesQuery} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {SettingsTemplate, SettingsTemplateHeader} from '@dms/settings/shared';
import {usePermissions} from '@dms/shared';

import {MarketingCodeDetailForm} from './components/MarketingCodeDetailForm';

export function WarehouseMarketingCodeDetail() {
  const {id} = useParams();

  const [warehouseDictionaryEdit, warehouseDictionaryCreate] = usePermissions({
    permissionKeys: ['warehouseDictionaryEdit', 'warehouseDictionaryCreate'],
  });

  const {
    data: marketingCode,
    isLoading: isMarketingCodeLoading,
    isError: isMarketingCodeError,
  } = useGetMarketingCodeQuery({marketingCodeId: id as string}, {skip: isNilOrEmpty(id)});

  const {
    data: warehouses,
    isLoading: isWarehousesLoading,
    isError: isWarehousesError,
  } = useGetWarehousesQuery();

  const isLoading = isNotNil(id)
    ? isWarehousesLoading || isMarketingCodeLoading
    : isWarehousesLoading;

  const isError = isNotNil(id) ? isWarehousesError || isMarketingCodeError : isWarehousesError;

  // Check edit permission only if id is not nil that means that the user is creating a new measurement unit
  const isEditDisabled = isNil(id) ? !warehouseDictionaryCreate : !warehouseDictionaryEdit;

  const header: SettingsTemplateHeader = {
    title: isNil(id) ? i18n.t('entity.warehouse.labels.newMarketingCode') : marketingCode?.name,
    alerts: isEditDisabled
      ? [
          {
            type: 'banner',
            variant: 'info',
            message: i18n.t('module.warehouse.label.missingWarehouseDictionaryEditPermission'),
          },
        ]
      : undefined,
  };

  return (
    <>
      <Helmet title={i18n.t('entity.warehouse.labels.marketingCodeDetail')} />
      <SettingsTemplate
        header={header}
        isLoading={isLoading}
        isError={isError}
        data-testid={testIds.settings.workshopPresetsCategory('template')}
      >
        <MarketingCodeDetailForm
          marketingCode={marketingCode}
          warehouses={warehouses}
          isEditDisabled={isEditDisabled}
          data-testid={testIds.settings.warehousesMarketingCodeDetail('marketingCodeDetailForm')}
        />
      </SettingsTemplate>
    </>
  );
}
